import { useState, useEffect, useRef } from 'react';
import { useToast } from '../../../../providers/ToastProvider';
import { SessionApi } from '../../../../apis/SessionApi';
import { isEmpty } from '../../../../utilities/generalUtils';

export function useGuestSession(data) {
    const { showToast } = useToast();

    const [myVehicle, setMyVehicle] = useState({});
    const [activeSession, setActiveSession] = useState({});
    const [showConfirmStop, setShowConfirmStop] = useState(false);
    const [sessionStoppedAwaitingComplete, setSessionStoppedAwaitingComplete] = useState(false);
    const [sessionCompletedDisplay, setSessionCompletedDisplay] = useState(false);
    const [stoppedSessionData, setStoppedSessionData] = useState(null);
    const [stoppedSessionUuid, setStoppedSessionUuid] = useState(null);
    const stopDisplayTimeoutRef = useRef(null);
    const stopRequestTimeoutRef = useRef(null);

    const clearStopDisplayState = (clearStoppedUuid = true) => {
        setSessionStoppedAwaitingComplete(false);
        setSessionCompletedDisplay(false);
        setStoppedSessionData(null);
        if (clearStoppedUuid) setStoppedSessionUuid(null);
        if (stopDisplayTimeoutRef.current) {
            clearTimeout(stopDisplayTimeoutRef.current);
            stopDisplayTimeoutRef.current = null;
        }
    };

    const handleEndChargeClick = () => {
        if (activeSession) {
            setShowConfirmStop(true);
        }
    };

    const handleConfirmStopCharge = () => {
        setShowConfirmStop(false);
        if (!activeSession?.session?.uuid) return;

        const sessionSnapshot = activeSession;
        setStoppedSessionData(sessionSnapshot);
        setStoppedSessionUuid(sessionSnapshot.session.uuid);
        setSessionStoppedAwaitingComplete(true);

        const requestTimeoutId = setTimeout(() => {
            stopRequestTimeoutRef.current = null;
            setSessionStoppedAwaitingComplete(false);
            setStoppedSessionData(null);
            setStoppedSessionUuid(null);
            showToast({
                type: 'error',
                title: 'Stop Request Failed',
                message: 'Stop charging request timed out. Please try again.',
            });
        }, 25000);
        stopRequestTimeoutRef.current = requestTimeoutId;

        SessionApi.stop(sessionSnapshot.session.uuid)
            .then(() => {
                if (stopRequestTimeoutRef.current) {
                    clearTimeout(stopRequestTimeoutRef.current);
                    stopRequestTimeoutRef.current = null;
                }
                stopDisplayTimeoutRef.current = setTimeout(() => {
                    stopDisplayTimeoutRef.current = null;
                    clearStopDisplayState(false);
                }, 15000);
                if (data?.loadDashboardSessionData) {
                    data.loadDashboardSessionData();
                }
            })
            .catch(() => {
                if (stopRequestTimeoutRef.current) {
                    clearTimeout(stopRequestTimeoutRef.current);
                    stopRequestTimeoutRef.current = null;
                }
                setSessionStoppedAwaitingComplete(false);
                setStoppedSessionData(null);
                setStoppedSessionUuid(null);
                showToast({
                    type: 'error',
                    title: 'Error',
                    message: 'Unable to stop charging. Please try again',
                });
            });
    };

    useEffect(() => {
        if (!data.userDashboardSessions) return;
        if (data.userDashboardSessions.activeSessions) {
            if (data.userDashboardSessions.activeSessions.length === 0) {
                setActiveSession({});
                setStoppedSessionUuid(null);
                if (sessionStoppedAwaitingComplete) {
                    setSessionStoppedAwaitingComplete(false);
                    setSessionCompletedDisplay(true);
                    if (stopDisplayTimeoutRef.current) {
                        clearTimeout(stopDisplayTimeoutRef.current);
                    }
                    stopDisplayTimeoutRef.current = setTimeout(() => {
                        stopDisplayTimeoutRef.current = null;
                        clearStopDisplayState(false);
                    }, 15000);
                }
            } else if (!sessionStoppedAwaitingComplete && !sessionCompletedDisplay) {
                setActiveSession(data.userDashboardSessions.activeSessions[0]);
            }
        }
    }, [data.userDashboardSessions, sessionStoppedAwaitingComplete]);

    useEffect(() => {
        if (sessionStoppedAwaitingComplete && data?.loadDashboardSessionData) {
            const pollInterval = setInterval(() => {
                data.loadDashboardSessionData();
            }, 2000);
            return () => clearInterval(pollInterval);
        }
    }, [sessionStoppedAwaitingComplete, data?.loadDashboardSessionData]);

    useEffect(() => {
        if (data.userVehicles?.length > 0) {
            setMyVehicle(data.userVehicles[0]);
        }
    }, [data.userVehicles]);

    useEffect(() => {
        return () => {
            if (stopDisplayTimeoutRef.current) clearTimeout(stopDisplayTimeoutRef.current);
            if (stopRequestTimeoutRef.current) clearTimeout(stopRequestTimeoutRef.current);
        };
    }, []);

    return {
        myVehicle,
        activeSession,
        showConfirmStop,
        sessionStoppedAwaitingComplete,
        sessionCompletedDisplay,
        stoppedSessionData,
        stoppedSessionUuid,
        isEmpty,
        handleEndChargeClick,
        handleConfirmStopCharge,
        clearStopDisplayState,
        setShowConfirmStop,
    };
}
